const DEFAULT_SCHOOL_CALLBACK_BASE_URL = 'https://realworld-school.org';
const SCHOOL_CALLBACK_PATH = '/api/oauth2_callback.php';

/**
 * 학교 콜백 베이스 URL을 반환합니다.
 * 우선순위: VITE_SCHOOL_CALLBACK_BASE_URL → DEFAULT_SCHOOL_CALLBACK_BASE_URL
 *
 * 환경 변수가 비어있거나 잘못된 URL 형식이면 기본 URL을 사용합니다.
 */
export function getSchoolCallbackBaseUrl(): string {
  const envValue =
    typeof import.meta !== 'undefined' && import.meta.env
      ? (import.meta.env.VITE_SCHOOL_CALLBACK_BASE_URL as string | undefined)
      : undefined;

  if (!envValue) return DEFAULT_SCHOOL_CALLBACK_BASE_URL;

  try {
    // URL 형식이 유효한지 검증
    // eslint-disable-next-line no-new
    new URL(envValue);
    // 끝의 / 제거 (덧붙일 때 // 가 되는 것을 방지)
    return envValue.replace(/\/+$/, '');
  } catch {
    return DEFAULT_SCHOOL_CALLBACK_BASE_URL;
  }
}

interface BuildSchoolCallbackUrlOptions {
  channelId: string;
  authCode: string;
  /**
   * 베이스 URL을 직접 지정하고 싶을 때 사용합니다. (테스트 용)
   * 비워두면 `getSchoolCallbackBaseUrl()` 의 결과를 사용합니다.
   */
  baseUrl?: string;
}

/**
 * 학교 콜백 URL을 조립합니다.
 *
 * 예시:
 *   https://realworld-school.org/api/oauth2_callback.php?channelId=ch_123&authCode=lk_abc
 */
export function buildSchoolCallbackUrl({
  channelId,
  authCode,
  baseUrl
}: BuildSchoolCallbackUrlOptions): string {
  if (!channelId) throw new Error('buildSchoolCallbackUrl: channelId is required');
  if (!authCode) throw new Error('buildSchoolCallbackUrl: authCode is required');

  const base = baseUrl ?? getSchoolCallbackBaseUrl();
  const url = new URL(SCHOOL_CALLBACK_PATH, base);
  url.searchParams.set('channelId', channelId);
  url.searchParams.set('authCode', authCode);
  return url.toString();
}

/**
 * 이메일 주소를 마스킹합니다.
 *
 * - 로컬 파트가 1글자: `u***@domain.com`
 * - 로컬 파트가 2글자: `us***@domain.com`
 * - 로컬 파트가 3글자 이상: 앞 1글자만 노출 + `***` + `@domain.com`
 *   (예: `user@gmail.com` → `u***@gmail.com`)
 *
 * 잘못된 형식이면 원본을 그대로 반환합니다.
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email || typeof email !== 'string') return '';

  const atIndex = email.lastIndexOf('@');
  if (atIndex <= 0 || atIndex === email.length - 1) {
    return email;
  }

  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex); // '@domain.com'

  if (local.length === 1) {
    return `${local}***${domain}`;
  }

  // 첫 글자만 노출
  return `${local.charAt(0)}***${domain}`;
}

/**
 * authCode 형식이 올바른지 검증합니다.
 * 학교 시스템에서 발급되는 authCode 형식: `lk_` + 32자의 16진수
 */
export const AUTH_CODE_PATTERN = /^lk_[a-f0-9]{32}$/;

export function isValidAuthCode(authCode: string | null | undefined): authCode is string {
  return typeof authCode === 'string' && AUTH_CODE_PATTERN.test(authCode);
}
